import { Button } from "@/components/ui/button";
import { useId } from "react";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import type { Post, RecurringCost, Split } from "../../domain/types.ts";

export type RecurringSplitSelection = Pick<RecurringCost, "splitMode" | "splits">;

interface Props extends RecurringSplitSelection {
  posts: Post[];
  onChange: (next: RecurringSplitSelection) => void;
}

export function RecurringSplitEditor({ splitMode, splits, posts, onChange }: Props) {
  const absorberGroup = useId();
  function changeSplit(index: number, changes: Partial<Split>) {
    onChange({ splitMode, splits: splits.map((split, i) => i === index ? { ...split, ...changes } : split) });
  }

  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">Destination posts</legend>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={splits.length === 0}
          onChange={(event) => onChange({
            splitMode,
            splits: event.target.checked
              ? []
              : [{ postId: posts[0]?.id ?? "", value: 100, absorbsRemainder: true }],
          })}
        />
        No post — keep this recurring cost unassigned
      </label>
      {splits.length > 1 && (
        <NativeSelect aria-label="Split by" value={splitMode} onChange={event => {
          const mode = event.target.value as RecurringCost["splitMode"];
          onChange({ splitMode: mode, splits: splits.map((split, index) => ({ ...split, value: mode === "percent" && index === 0 ? 100 : 0 })) });
        }}>
          <option value="percent">Percentages</option>
          <option value="fixed">Amounts</option>
        </NativeSelect>
      )}
      {splits.map((split, index) => (
        <div key={index} className="flex flex-wrap items-center gap-2">
          <NativeSelect
            aria-label={index === 0 ? "Destination post" : `Destination post ${index + 1}`}
            className="min-w-36 flex-1"
            value={split.postId}
            onChange={event => changeSplit(index, { postId: event.target.value })}
          >
            <option value="">Choose a post…</option>
            {posts.map(post => <option key={post.id} value={post.id}>{post.name}</option>)}
          </NativeSelect>
          {splits.length > 1 && (
            <>
              <Input aria-label={`Split value ${index + 1}`} type="number" step="0.01" className="w-24" value={split.value} onChange={event => changeSplit(index, { value: Number(event.target.value) || 0 })} />
              <span className="text-xs text-budget-ink-muted">{splitMode === "percent" ? "%" : "amount"}</span>
              <label className="flex items-center gap-1 text-xs"><input type="radio" name={absorberGroup} checked={split.absorbsRemainder} onChange={() => onChange({ splitMode, splits: splits.map((item, i) => ({ ...item, absorbsRemainder: i === index })) })} />rounding</label>
              <Button type="button" size="xs" variant="ghost" onClick={() => {
                const remaining = splits.filter((_, i) => i !== index);
                if (!remaining.some(item => item.absorbsRemainder)) remaining[0] = { ...remaining[0]!, absorbsRemainder: true };
                onChange({ splitMode, splits: remaining });
              }}>remove</Button>
            </>
          )}
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange({ splitMode, splits: [...splits, { postId: "", value: splits.length === 0 ? 100 : 0, absorbsRemainder: splits.length === 0 }] })}>Add another post</Button>
    </fieldset>
  );
}
